/**
 * QAPanel — runs and displays the formatter_v2 Visual QA report.
 * Sits behind a "Run QA" button in the BookEditor TopBar. Each issue is
 * grouped by severity (error / warning / info) with a page + short message.
 *
 * If the report includes a ``fingerprint_diff`` (from a previous run) we
 * surface added / removed / changed chapters as a small summary at the top.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Loader2, ShieldCheck, AlertTriangle, AlertOctagon, Info,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SEV_META = {
  error:   { icon: AlertOctagon,   color: "text-red-700 bg-red-50 border-red-200",
             label: "Errors" },
  warning: { icon: AlertTriangle,  color: "text-amber-800 bg-amber-50 border-amber-200",
             label: "Warnings" },
  info:    { icon: Info,           color: "text-slate-700 bg-slate-50 border-slate-200",
             label: "Info" },
};

export function QAPanel({ bookId, stylePack = "classic_trade" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);

  // Load the cached QA report on mount so users see the last known state
  // without having to re-run (which is a ~10 s render on large books).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}/qa`);
        if (!cancelled && data?.report) {
          setReport(data.report);
          setLastRunAt(data.run_at || null);
        }
      } catch (err) { /* no cached report yet — that's fine */ }
    })();
    return () => { cancelled = true; };
  }, [bookId]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/qa`, null, {
        params: { style_pack: stylePack },
      });
      setReport(data);
      setLastRunAt(new Date().toISOString());
      setOpen(true);
      const counts = data.counts || {};
      const parts = [];
      if (counts.error)   parts.push(`${counts.error} error(s)`);
      if (counts.warning) parts.push(`${counts.warning} warning(s)`);
      if (counts.info)    parts.push(`${counts.info} note(s)`);
      toast.success(
        parts.length ? `QA finished — ${parts.join(", ")}` : "QA finished — no issues found"
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const grouped = { error: [], warning: [], info: [] };
  for (const iss of (report?.issues || [])) grouped[iss.severity]?.push(iss);
  const diff = report?.fingerprint_diff;
  const counts = report?.counts || {};

  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="btn-ghost inline-flex items-center gap-2 text-xs px-2.5 py-1"
          data-testid="qa-run-btn"
          title="Run visual QA on the current PDF export — flags empty pages, orphan lines, short chapters"
        >
          {busy
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/>
            : <ShieldCheck className="w-4 h-4" strokeWidth={1.5}/>}
          {busy ? "Running QA…" : "Run QA"}
        </button>
        {!busy && report && !open && (
          <button
            type="button"
            onClick={()=>setOpen(true)}
            className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--primary)] link-underline"
            data-testid="qa-view-last"
            title={lastRunAt ? `Last run: ${new Date(lastRunAt).toLocaleString()}` : ""}
          >
            View last
          </button>
        )}
      </div>

      {open && report && (
        <div className="fixed inset-0 z-50 bg-black/60 flex" data-testid="qa-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="ml-auto w-full max-w-3xl bg-white shadow-2xl border-l border-[color:var(--border)] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-[color:var(--border)] px-6 py-3 flex items-center justify-between z-10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                  § Visual QA report
                </p>
                <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
                  {report.total_pages} pages · avg {report.stats?.avg_chars_per_page} chars/pg ·{" "}
                  {Object.keys(report.chapter_fingerprints || {}).length} chapters fingerprinted
                </p>
              </div>
              <button onClick={()=>setOpen(false)}
                className="btn-ghost text-sm inline-flex items-center gap-1"
                data-testid="qa-close">
                <X className="w-4 h-4"/> Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary tiles */}
              <div className="grid grid-cols-3 gap-3">
                {["error","warning","info"].map((sev) => {
                  const meta = SEV_META[sev];
                  const Icon = meta.icon;
                  return (
                    <div key={sev} className={`border ${meta.color} px-3 py-2`} data-testid={`qa-count-${sev}`}>
                      <p className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">
                        <Icon className="w-3 h-3" strokeWidth={2}/> {meta.label}
                      </p>
                      <p className="text-2xl font-light mt-0.5">{counts[sev] || 0}</p>
                    </div>
                  );
                })}
              </div>

              {/* Fingerprint diff (baseline compare) */}
              {diff && (diff.added?.length || diff.removed?.length || diff.changed?.length) ? (
                <div className="border border-[color:var(--border)] bg-[color:var(--canvas)] p-4" data-testid="qa-diff">
                  <p className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">§ Baseline diff (vs previous QA)</p>
                  {diff.added?.length ? <p className="text-sm text-emerald-700">+ Added: {diff.added.join(", ")}</p> : null}
                  {diff.removed?.length ? <p className="text-sm text-red-700">− Removed: {diff.removed.join(", ")}</p> : null}
                  {diff.changed?.length ? <p className="text-sm text-amber-800">~ Changed: {diff.changed.join(", ")}</p> : null}
                  <p className="text-xs text-[color:var(--text-muted)] mt-1">
                    {diff.unchanged?.length || 0} chapters unchanged
                  </p>
                </div>
              ) : diff ? (
                <div className="border border-emerald-200 bg-emerald-50 p-4" data-testid="qa-diff">
                  <p className="text-sm text-emerald-800">✓ No chapter content changed since last QA run.</p>
                </div>
              ) : null}

              {/* Issue lists, one per severity */}
              {["error","warning","info"].map((sev) => {
                const list = grouped[sev];
                if (!list?.length) return null;
                const meta = SEV_META[sev];
                const Icon = meta.icon;
                return (
                  <div key={sev} data-testid={`qa-issues-${sev}`}>
                    <p className={`text-xs uppercase tracking-widest inline-flex items-center gap-1 ${sev==='error'?'text-red-700':sev==='warning'?'text-amber-800':'text-slate-700'}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={2}/> {meta.label} ({list.length})
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {list.map((iss, i) => (
                        <div key={`${iss.code}-${iss.page}-${i}`}
                             className={`border ${meta.color} px-3 py-2 text-sm flex gap-3`}>
                          <span className="font-mono text-xs w-14 shrink-0 opacity-70">p.{iss.page}</span>
                          <span className="flex-1">
                            {iss.message}
                            {iss.chapter_title && (
                              <span className="block text-xs opacity-70 mt-0.5">
                                Chapter: {iss.chapter_title}
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] font-mono opacity-50 shrink-0">{iss.code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* No issues at all */}
              {counts.error === 0 && counts.warning === 0 && counts.info === 0 && (
                <div className="border border-emerald-200 bg-emerald-50 p-6 text-center">
                  <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600 mb-2" strokeWidth={1.5}/>
                  <p className="text-sm text-emerald-800">Clean bill of health — no issues found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
